import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, CardContent, Divider, Typography } from '@mui/material'
import { useParams } from 'react-router'
import { Helmet } from '@dr.pogodin/react-helmet'

import CustomerApi from '../../../api/CustomerApi'
import useAuth from '../../../hooks/useAuth'
import UserApi from '../../../api/UserApi'
import UsersGrid from '../../../components/entities/UsersGrid'
import CustomerDialog from './CustomerDialog'
import {
    userHasManyDevices,
    UserListItem,
    USER_MULTI_DEVICE_THRESHOLD,
    CustomerSingleItem,
} from '@finance-lab/utils/types'

const customerApi = new CustomerApi()
const userApi = new UserApi()

const CustomerDetailsForm = () => {
    const { customerId } = useParams()
    const [customer, setCustomer] = useState<CustomerSingleItem | null>(null)
    const [users, setUsers] = useState<UserListItem[] | null>(null)
    const [customerDialogOpen, setCustomerDialogOpen] = useState<boolean>(false)
    const { isAuthenticated } = useAuth()

    useEffect(() => {
        const fetchCustomer = async () => {
            if (!isAuthenticated || !customerId) return
            const response = await customerApi.getCustomer(customerId)
            setCustomer(response)
        }
        void fetchCustomer()
    }, [customerId, isAuthenticated])

    const loadUsers = useCallback(async () => {
        if (!isAuthenticated || !customerId) return
        const response = await userApi.adminFindUsersForCustomer(customerId)
        setUsers(response.data)
    }, [customerId, isAuthenticated])

    useEffect(() => {
        void loadUsers()
    }, [loadUsers])

    if (!customer) {
        return null
    }

    const usersWithManyDevices = users?.filter((user) => userHasManyDevices(user.deviceCount ?? 0)) ?? []

    const handleSetUsers = (newUsers: UserListItem[]) => {
        const usersForThisCustomer = newUsers.filter((user) => user.customerId === customer.id)
        setUsers(usersForThisCustomer)
    }

    return (
        <>
            <Helmet title={`Kund: ${customer.name}`} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                Kund: {customer.name}
            </Typography>
            <Button
                variant='outlined'
                size='small'
                color='primary'
                sx={{ mb: 6 }}
                onClick={() => setCustomerDialogOpen(true)}
            >
                Redigera
            </Button>
            <Divider sx={{ my: 6 }} />
            {usersWithManyDevices.length > 0 && (
                <Alert severity='warning' variant='filled' sx={{ mb: 4 }}>
                    {usersWithManyDevices.length} användare med fler än {USER_MULTI_DEVICE_THRESHOLD} enheter:{' '}
                    {usersWithManyDevices.map((u) => u.name).join(', ')}
                </Alert>
            )}
            <Card sx={{ mb: 6 }}>
                <CardContent>
                    <UsersGrid
                        fields={['name', 'email', 'deviceCount', 'lastActive', 'actions']}
                        users={users}
                        setUsers={handleSetUsers}
                        highlightManyDevices
                        onRefreshPage={loadUsers}
                    />
                </CardContent>
            </Card>
            <CustomerDialog
                customer={customer}
                open={customerDialogOpen}
                onClose={() => setCustomerDialogOpen(false)}
                onSubmit={async (customerData) => {
                    await customerApi.updateCustomer(customer.id, customerData)
                    setCustomer({ ...customerData, id: customer.id })
                    setCustomerDialogOpen(false)
                }}
            />
        </>
    )
}

export default CustomerDetailsForm
